/**
 * Accreditation API Service
 * Frontend API client for ICSC Accreditation System
 */
import axiosClient from './axiosClient';

const API_URL = import.meta.env.VITE_API_URL;
const API_BASE = `${API_URL}/accreditation`;

// ============================================================================
// CERTIFICATION BODY ACCREDITATION
// ============================================================================

export const applyCBAccreditation = async (data) => {
  const response = await axiosClient.post(`${API_BASE}/cert-bodies/apply`, data);
  return response.data;
};

export const listCBAccreditations = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status_filter', filters.status);
  if (filters.scope_id) params.append('scope_id', filters.scope_id);
  if (filters.skip) params.append('skip', filters.skip);
  if (filters.limit) params.append('limit', filters.limit);

  const response = await axiosClient.get(`${API_BASE}/cert-bodies?${params}`);
  return response.data;
};

export const getCBAccreditation = async (accreditationId) => {
  const response = await axiosClient.get(`${API_BASE}/cert-bodies/${accreditationId}`);
  return response.data;
};

export const makeCBDecision = async (accreditationId, newStatus, decisionNotes, reason) => {
  const params = new URLSearchParams();
  params.append('new_status', newStatus);
  if (decisionNotes) params.append('decision_notes', decisionNotes);
  if (reason) params.append('reason', reason);

  const response = await axiosClient.post(
    `${API_BASE}/cert-bodies/${accreditationId}/decision?${params}`
  );
  return response.data;
};

export const checkCanIssueCertificates = async (companyId, isoStandardId = null) => {
  const params = new URLSearchParams();
  if (isoStandardId) params.append('iso_standard_id', isoStandardId);

  const response = await axiosClient.get(
    `${API_BASE}/cert-bodies/${companyId}/can-issue-certificates?${params}`
  );
  return response.data;
};

// ============================================================================
// AUDITOR ACCREDITATION
// ============================================================================

export const applyAuditorAccreditation = async (data) => {
  const response = await axiosClient.post(`${API_BASE}/auditors/apply`, data);
  return response.data;
};

export const listAuditorAccreditations = async (userId = null) => {
  const params = userId ? `?user_id=${userId}` : '';
  const response = await axiosClient.get(`${API_BASE}/auditors${params}`);
  return response.data;
};

export const getAuditorAccreditation = async (accreditationId) => {
  const response = await axiosClient.get(`${API_BASE}/auditors/${accreditationId}`);
  return response.data;
};

export const makeAuditorDecision = async (accreditationId, newStatus, decisionNotes, reason) => {
  const params = new URLSearchParams();
  params.append('new_status', newStatus);
  if (decisionNotes) params.append('decision_notes', decisionNotes);
  if (reason) params.append('reason', reason);

  const response = await axiosClient.post(
    `${API_BASE}/auditors/${accreditationId}/decision?${params}`
  );
  return response.data;
};

export const getAuditorCompetencies = async (userId) => {
  const response = await axiosClient.get(`${API_BASE}/auditors/${userId}/competencies`);
  return response.data;
};

export const verifyAuditorCompetency = async (userId, scopeId) => {
  const response = await axiosClient.get(
    `${API_BASE}/auditors/${userId}/competency-verification/${scopeId}`
  );
  return response.data;
};

// ============================================================================
// COURSE ACCREDITATION
// ============================================================================

export const applyCourseAccreditation = async (data) => {
  const response = await axiosClient.post(`${API_BASE}/courses/apply`, data);
  return response.data;
};

export const getCourseAccreditation = async (accreditationId) => {
  const response = await axiosClient.get(`${API_BASE}/courses/${accreditationId}`);
  return response.data;
};

// ============================================================================
// SCOPES & RULES
// ============================================================================

export const getAccreditationScopes = async (isActive = true) => {
  const params = isActive !== null ? `?is_active=${isActive}` : '';
  const response = await axiosClient.get(`${API_BASE}/scopes${params}`);
  return response.data;
};

export const getAccreditationRules = async (accreditationType = null, isActive = true) => {
  const params = new URLSearchParams();
  if (accreditationType) params.append('accreditation_type', accreditationType);
  if (isActive !== null) params.append('is_active', isActive.toString());

  const response = await axiosClient.get(`${API_BASE}/rules?${params}`);
  return response.data;
};

// ============================================================================
// SURVEILLANCE
// ============================================================================

export const getUpcomingSurveillance = async (daysAhead = 30) => {
  const response = await axiosClient.get(`${API_BASE}/surveillance/upcoming?days_ahead=${daysAhead}`);
  return response.data;
};

export const scheduleSurveillance = async (data) => {
  const response = await axiosClient.post(`${API_BASE}/surveillance/schedule`, data);
  return response.data;
};

export const recordSurveillance = async (scheduleId, data) => {
  const response = await axiosClient.post(`${API_BASE}/surveillance/${scheduleId}/record`, data);
  return response.data;
};

// ============================================================================
// DASHBOARD
// ============================================================================

export const getAccreditationSummary = async () => {
  const response = await axiosClient.get(`${API_BASE}/summary`);
  return response.data;
};

// ============================================================================
// STATUS CONSTANTS
// ============================================================================

export const AccreditationStatus = {
  APPLIED: 'applied',
  UNDER_REVIEW: 'under_review',
  DOCUMENT_REVIEW: 'document_review',
  INITIAL_ASSESSMENT: 'initial_assessment',
  WITNESS_AUDIT: 'witness_audit',
  TECHNICAL_REVIEW: 'technical_review',
  GRANTED: 'granted',
  SUSPENDED: 'suspended',
  WITHDRAWN: 'withdrawn',
  EXPIRED: 'expired',
  REJECTED: 'rejected'
};

export const AccreditationType = {
  CERTIFICATION_BODY: 'certification_body',
  AUDITOR: 'auditor',
  COURSE: 'course'
};

// ============================================================================
// HELPERS
// ============================================================================

export const getStatusColor = (status) => {
  const colors = {
    applied: '#3498db',
    under_review: '#f39c12',
    document_review: '#f39c12',
    initial_assessment: '#f39c12',
    witness_audit: '#f39c12',
    technical_review: '#f39c12',
    granted: '#27ae60',
    suspended: '#e74c3c',
    withdrawn: '#95a5a6',
    expired: '#95a5a6',
    rejected: '#e74c3c'
  };
  return colors[status] || '#95a5a6';
};

export const getStatusLabel = (status) => {
  const labels = {
    applied: 'Applied',
    under_review: 'Under Review',
    document_review: 'Document Review',
    initial_assessment: 'Initial Assessment',
    witness_audit: 'Witness Audit',
    technical_review: 'Technical Review',
    granted: 'Granted',
    suspended: 'Suspended',
    withdrawn: 'Withdrawn',
    expired: 'Expired',
    rejected: 'Rejected'
  };
  return labels[status] || status;
};

export const formatAccreditationNumber = (number) => {
  return number || 'Pending';
};

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};
